import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CalendarCheck2, ImageIcon, Sparkles, Printer, BellRing, BookOpenText, Users2, Wallet, TrendingDown, TrendingUp, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const todayISO = () => new Date().toISOString().slice(0, 10);
const offsetISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const monthStartISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function StaffDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, total: 0, gallery: 0, events: 0, remindersTomorrow: 0 });
  const [briefs, setBriefs] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [payoutsMode, setPayoutsMode] = useState("last7"); // last7 | this_month
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  const loadDigest = async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/staff/notifications/approvals-digest/preview");
      setDigestPreview(res.data);
    } catch { /* silent */ }
  };

  const sendDigest = async () => {
    if (!digestPreview?.will_send) {
      toast.info("Nothing pending — no digest to send");
      return;
    }
    setDigestSending(true);
    try {
      const res = await api.post("/staff/notifications/approvals-digest");
      if (res.data.ok) toast.success("Digest sent to admin WhatsApp");
      else if (res.data.skipped) toast.info(res.data.reason || "Skipped");
      else toast.error(res.data.error || "Send failed");
      loadDigest();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setDigestSending(false);
    }
  };

  const loadPayouts = async (mode) => {
    const [s, e] = mode === "this_month"
      ? [monthStartISO(), todayISO()]
      : [offsetISO(-6), todayISO()];
    try {
      const res = await api.get("/staff/procurement/payouts", { params: { start_date: s, end_date: e } });
      setPayouts({ ...res.data, _start: s, _end: e });
    } catch (err) { console.error("Payouts summary failed:", err); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [res, gal, ev, rem, br] = await Promise.all([
          api.get("/staff/reservations"),
          api.get("/staff/gallery"),
          api.get("/staff/events"),
          api.get("/staff/reservations/reminders?days=1"),
          api.get("/staff/briefs?limit=10"),
        ]);
        const list = res.data;
        setStats({
          pending: list.filter((r) => r.status === "pending").length,
          confirmed: list.filter((r) => r.status === "confirmed").length,
          total: list.length,
          gallery: gal.data.length,
          events: ev.data.length,
          remindersTomorrow: rem.data.length,
        });
        setBriefs(br.data);
      } catch (err) { console.error("Dashboard load failed:", err); }
    })();
    loadPayouts(payoutsMode);
    loadDigest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchMode = (m) => { setPayoutsMode(m); loadPayouts(m); };

  return (
    <div data-testid="staff-dashboard">
      <div className="eyebrow">Overview</div>
      <h1 className="mt-2 text-4xl">Good day, back office.</h1>
      <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
        Quick view of what&apos;s happening at The Barracks Bistro right now.
      </p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Pending" value={stats.pending} testid="stat-pending" accent />
        <Stat label="Confirmed" value={stats.confirmed} testid="stat-confirmed" />
        <Stat label="Tomorrow's bookings" value={stats.remindersTomorrow} testid="stat-reminders" />
        <Stat label="Events posted" value={stats.events} testid="stat-events" />
      </div>

      <TomorrowSeatsTile />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <QuickLink to="/staff/reservations" icon={CalendarCheck2} title="Reservations" desc="Confirm, decline, and check the diary." />
        <QuickLink to="/staff/reminders" icon={BellRing} title="Reminders" desc="Send day-before WhatsApp reminders to guests." />
        <QuickLink to="/staff/menu" icon={BookOpenText} title="Menu" desc="Edit à-la-carte dishes, prices, and sections." />
        <QuickLink to="/staff/kitchen-brief" icon={Printer} title="Kitchen Brief" desc="Print a package sheet for tonight's function." />
        <QuickLink to="/staff/gallery" icon={ImageIcon} title="Gallery" desc="Edit captions, toggle visibility, upload photos." />
        <QuickLink to="/staff/events" icon={Sparkles} title="Events" desc="Post new events. Enable / disable." />
      </div>

      {/* Vendor payouts snapshot */}
      <div className="mt-12" data-testid="dashboard-payouts">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow">Vendor payouts</div>
            <h2 className="mt-2 display text-2xl">Payables snapshot</h2>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && digestPreview && (
              <button
                data-testid="send-approvals-digest"
                onClick={sendDigest}
                disabled={digestSending || !digestPreview.will_send}
                title={digestPreview.will_send ? "Sends the pending PO/GRN digest to admin WhatsApp" : "Nothing pending"}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={12} /> {digestSending ? "Sending…" : (digestPreview.will_send ? `Send digest (${digestPreview.po_pending + digestPreview.grn_pending})` : "Nothing pending")}
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="send-weekly-digest"
                onClick={async () => {
                  if (!window.confirm("Send weekly digest to admin WhatsApp now?")) return;
                  try {
                    const r = await api.post("/staff/notifications/weekly-digest");
                    if (r.data.ok) toast.success("Weekly digest sent");
                    else toast.error(r.data.error || "Send failed");
                  } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
                }}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors"
              >
                <Send size={12} /> Weekly digest
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="run-backup"
                onClick={async () => {
                  if (!window.confirm("Run a database backup to object storage now?")) return;
                  try {
                    const r = await api.post("/staff/backups/run");
                    if (r.data.ok) toast.success(`Backup saved · ${Math.round(r.data.size_bytes / 1024)} KB · ${r.data.collections} collections`);
                    else toast.error(r.data.error || "Backup failed");
                  } catch (err) { toast.error(err?.response?.data?.detail || "Backup failed"); }
                }}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))] transition-colors"
              >
                <Send size={12} /> Backup now
              </button>
            )}
            <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-0.5 bg-[hsl(var(--bg-2))]">
              <button
                data-testid="dashboard-payouts-last7"
                onClick={() => switchMode("last7")}
                className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full ${payoutsMode === "last7" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
              >Last 7 days</button>
              <button
                data-testid="dashboard-payouts-this-month"
                onClick={() => switchMode("this_month")}
                className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full ${payoutsMode === "this_month" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
              >This month</button>
            </div>
            <Link to="/staff/procurement" className="text-sm text-[hsl(var(--brass-2))] hover:underline">Open →</Link>
          </div>
        </div>
        {!payouts ? (
          <div className="mt-4 p-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-sm text-[hsl(var(--ink-soft))]">Loading…</div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <PayTile testid="dash-pay-grn" icon={TrendingUp} label="GRN in range" value={inr(payouts.summary.grn_in_range)} />
              <PayTile testid="dash-pay-paid" icon={TrendingDown} label="Paid in range" value={inr(payouts.summary.paid_in_range)} accent="forest" />
              <PayTile testid="dash-pay-outstanding" icon={Wallet} label="Outstanding" value={inr(payouts.summary.outstanding)} accent={Number(payouts.summary.outstanding) > 0 ? "red" : "forest"} />
              <PayTile testid="dash-pay-vendors" icon={Users2} label="Vendors with dues" value={String(payouts.summary.vendors_with_dues)} />
            </div>
            <div className="mt-2 text-[10px] text-[hsl(var(--ink-soft))]">
              Range {payouts._start} → {payouts._end}. Outstanding is the live all-time position.
            </div>
          </>
        )}
      </div>

      {/* Recent Briefs — audit trail of every kitchen brief printed */}
      <div className="mt-12" data-testid="dashboard-recent-briefs">
        <div className="flex items-end justify-between">
          <div>
            <div className="eyebrow">Kitchen dispatch log</div>
            <h2 className="mt-2 display text-2xl">Recent briefs</h2>
          </div>
          <Link to="/staff/kitchen-brief" className="text-sm text-[hsl(var(--brass-2))] hover:underline">New brief →</Link>
        </div>

        {briefs.length === 0 ? (
          <div data-testid="briefs-empty" className="mt-4 p-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] text-center text-sm text-[hsl(var(--ink-soft))]">
            <Users2 size={20} className="mx-auto text-[hsl(var(--brass))]" />
            <div className="mt-2">No kitchen briefs printed yet. Once staff prints one, it will show up here.</div>
          </div>
        ) : (
          <div className="mt-4 border border-[hsl(var(--rule))] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--bg-2))] text-left text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
                <tr>
                  <th className="p-3">KOT</th>
                  <th className="p-3">Event</th>
                  <th className="p-3">Date · Time</th>
                  <th className="p-3">Pax</th>
                  <th className="p-3">Dishes</th>
                  <th className="p-3">Printed by</th>
                  <th className="p-3">When</th>
                </tr>
              </thead>
              <tbody>
                {briefs.map((b) => (
                  <tr key={b.id} data-testid={`brief-row-${b.kot_label}`} className="border-t border-[hsl(var(--rule))]">
                    <td className="p-3 font-mono font-semibold text-[hsl(var(--forest))]">{b.kot_label}</td>
                    <td className="p-3">
                      <div>{b.event_name || <span className="text-[hsl(var(--ink-soft))] italic">—</span>}</div>
                      {b.host && <div className="text-[10px] text-[hsl(var(--ink-soft))]">host: {b.host}</div>}
                      {b.package_tagline && <div className="text-[10px] text-[hsl(var(--ink-soft))]">{b.package_tagline}</div>}
                    </td>
                    <td className="p-3 text-[hsl(var(--ink-soft))]">
                      {b.date || "—"}{b.time ? ` · ${b.time}` : ""}
                    </td>
                    <td className="p-3">{b.pax || "—"}</td>
                    <td className="p-3">{b.dish_count}</td>
                    <td className="p-3 text-[hsl(var(--ink-soft))]">{b.printed_by}</td>
                    <td className="p-3 text-[hsl(var(--ink-soft))]">{new Date(b.printed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={`p-5 border ${accent ? "border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.06)]" : "border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]"}`}>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 display text-4xl text-[hsl(var(--forest))]">{value}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="group border border-[hsl(var(--rule))] p-6 flex items-start gap-4 hover:border-[hsl(var(--brass))] transition-colors">
      <Icon size={22} className="mt-1 text-[hsl(var(--brass))]" />
      <div>
        <div className="text-lg font-semibold group-hover:text-[hsl(var(--brass-2))]">{title}</div>
        <div className="text-sm text-[hsl(var(--ink-soft))] mt-1">{desc}</div>
      </div>
    </Link>
  );
}

function PayTile({ label, value, testid, accent = "ink", icon: Icon }) {
  const color = {
    ink: "text-[hsl(var(--ink))]",
    forest: "text-[hsl(var(--forest))]",
    brass: "text-[hsl(var(--brass-2))]",
    red: "text-[hsl(6_62%_45%)]",
  }[accent] || "text-[hsl(var(--ink))]";
  return (
    <div data-testid={testid} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
        {Icon && <Icon size={14} className="text-[hsl(var(--brass))]" />}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}


function TomorrowSeatsTile() {
  const tomorrowISO = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/staff/reservations/day-summary", { params: { date: tomorrowISO } });
        setData(res.data);
      } catch (err) { console.error("Day summary failed:", err); }
      finally { setLoading(false); }
    })();
  }, [tomorrowISO]);

  const label = new Date(tomorrowISO).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" });

  if (loading) {
    return (
      <section data-testid="tomorrow-seats" className="mt-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-5">
        <div className="text-[hsl(var(--ink-soft))] text-sm">Loading tomorrow…</div>
      </section>
    );
  }
  if (!data) return null;

  const totalCap = data.slots.reduce((s, x) => s + x.cap, 0);
  const filledPct = totalCap > 0 ? Math.round((data.booked_pax / totalCap) * 100) : 0;
  const busyClass = filledPct >= 80 ? "text-[hsl(6_62%_45%)]" : filledPct >= 50 ? "text-[hsl(var(--brass-2))]" : "text-[hsl(var(--forest))]";

  return (
    <section data-testid="tomorrow-seats" className="mt-10 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow">Tomorrow</div>
          <h2 className="mt-1 display text-2xl">{label} · {data.remaining_pax} seats free</h2>
          <div className="text-xs text-[hsl(var(--ink-soft))]">
            {data.booked_pax} pax already booked across {data.reservations} reservation{data.reservations === 1 ? "" : "s"} ·
            {" "}slot cap {data.slot_cap} ({data.is_weekend ? "weekend" : "weekday"})
          </div>
        </div>
        <div className={`text-right ${busyClass}`}>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Filled</div>
          <div className="display text-2xl">{filledPct}%</div>
        </div>
      </div>

      {data.day_blocked ? (
        <div data-testid="tomorrow-blocked" className="mt-4 p-3 border border-[hsl(6_62%_45%)] bg-[hsl(6_62%_45%)]/10 text-sm text-[hsl(6_62%_45%)]">
          <span className="font-semibold">Day blocked.</span> No public bookings will be accepted for {label}.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-4 md:grid-cols-8 gap-1.5">
          {data.slots.map((s) => {
            const filled = s.cap > 0 ? Math.round((s.booked / s.cap) * 100) : 0;
            const tone = s.blocked ? "bg-[hsl(6_62%_45%)]/20 border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]"
              : filled >= 80 ? "bg-[hsl(6_62%_45%)]/10 border-[hsl(6_62%_45%)]/40 text-[hsl(6_62%_45%)]"
              : filled >= 50 ? "bg-[hsl(var(--brass))]/10 border-[hsl(var(--brass))]/40 text-[hsl(var(--brass-2))]"
              : "bg-[hsl(var(--forest))]/10 border-[hsl(var(--forest))]/30 text-[hsl(var(--forest))]";
            const guests = s.guests || [];
            const hasDetails = !s.blocked && guests.length > 0;
            const tile = (
              <div
                data-testid={`tomorrow-slot-${s.time}`}
                className={`border rounded p-1.5 text-center ${tone} ${hasDetails ? "cursor-pointer hover:opacity-90 transition-opacity" : ""}`}
              >
                <div className="text-[10px] font-mono opacity-80">{s.time}</div>
                <div className="text-sm font-semibold mt-0.5">
                  {s.blocked ? "×" : `${s.remaining}`}
                </div>
                <div className="text-[9px] opacity-70">
                  {s.blocked ? "blocked" : `of ${s.cap}`}
                </div>
              </div>
            );
            if (!hasDetails) {
              const title = s.blocked ? s.blocked_reason || "Blocked" : `${s.booked}/${s.cap} pax · no reservations yet`;
              return <div key={s.time} title={title}>{tile}</div>;
            }
            return (
              <Popover key={s.time}>
                <PopoverTrigger asChild>{tile}</PopoverTrigger>
                <PopoverContent
                  data-testid={`tomorrow-slot-popover-${s.time}`}
                  align="center"
                  className="w-72 p-0"
                >
                  <div className="p-3 border-b border-[hsl(var(--rule))]">
                    <div className="flex items-baseline justify-between">
                      <div className="font-mono text-sm font-semibold">{s.time}</div>
                      <div className="text-xs text-[hsl(var(--ink-soft))]">{s.booked}/{s.cap} pax</div>
                    </div>
                    <div className="text-[10px] text-[hsl(var(--ink-soft))] mt-0.5">
                      {guests.length} reservation{guests.length === 1 ? "" : "s"} · {s.remaining} seats free
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-[hsl(var(--rule))]">
                    {guests.map((g, i) => (
                      <div key={g.id || i} className="p-2.5 text-xs" data-testid={`tomorrow-slot-guest-${s.time}-${i}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-semibold truncate">{g.name}</div>
                          <div className="shrink-0 text-[hsl(var(--ink-soft))]">{g.party_size} pax</div>
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[hsl(var(--ink-soft))]">
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider ${
                              g.status === "confirmed"
                                ? "bg-[hsl(var(--forest))]/15 text-[hsl(var(--forest))]"
                                : "bg-[hsl(var(--brass))]/15 text-[hsl(var(--brass-2))]"
                            }`}
                          >
                            {g.status}
                          </span>
                          {g.phone && (
                            <a
                              href={`tel:${g.phone}`}
                              className="font-mono hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {g.phone}
                            </a>
                          )}
                        </div>
                        {g.occasion && (
                          <div className="mt-1 text-[10px] italic text-[hsl(var(--ink-soft))] truncate">
                            {g.occasion}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="p-2 border-t border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
                    <Link
                      to="/staff/reservations"
                      className="text-[11px] text-[hsl(var(--brass-2))] hover:underline"
                      data-testid={`tomorrow-slot-view-all-${s.time}`}
                    >
                      Open Reservations →
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>
      )}
    </section>
  );
}
